import Image from "@tiptap/extension-image";
import { Editor, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import useDebounce from "@components/use-debounce/use-debounce";
import { classNames } from "@helpers/css";

import { uploadImagePlugin } from "./image-upload";

/**
 * Tiptap Extension to upload images
 * @see  https://gist.github.com/slava-vishnyakov/16076dff1a77ddaca93c4bccd4ec4521#gistcomment-3744392
 * @since 7th July 2021
 *
 * https://github.com/ueberdosis/tiptap/blob/main/packages/extension-image/src/image.ts
 *
 * Matches following attributes in Markdown-typed image: [, alt, src, title]
 *
 * Example:
 * ![Lorem](image.jpg) -> [, "Lorem", "image.jpg"]
 * ![](image.jpg "Ipsum") -> [, "", "image.jpg", "Ipsum"]
 * ![Lorem](image.jpg "Ipsum") -> [, "Lorem", "image.jpg", "Ipsum"]
 */

const minWidthImage = 50;
const maxWidthImage = 1000;

const ImageComponent = memo(
  ({
    node,
    updateAttributes,
    selected,
  }: {
    node: any;
    updateAttributes: (attributes: any) => void;
    selected: boolean;
    deleteNode: () => void;
    editor: Editor;
  }) => {
    const ref = useRef(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const attrWidth = node.attrs.width;
    const debouncedAttrWidth = useDebounce(attrWidth, 500);
    const widthRef = useRef<number | string | undefined>(attrWidth);
    const [width, setWidth] = useState<number | string | undefined>(attrWidth);

    const handleMouseUp = useCallback(() => {
      const validWidth = widthRef.current
        ? parseInt(String(widthRef.current))
        : undefined;
      if (validWidth) {
        updateAttributes({ width: validWidth });
      }
    }, [updateAttributes]);

    const imgRef = useRef(null);

    const startResize = (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      const parentWidth = parentRef.current?.clientWidth || false;
      if (!parentWidth) return;
      const startX = e.clientX;
      const startWidth = widthRef.current
        ? parseInt(String(widthRef.current))
        : parentWidth;

      const onMouseMove = (ev: any) => {
        const newWidth = startWidth + (ev.clientX - startX);
        const validWidth = Math.min(
          parentWidth || maxWidthImage,
          Math.max(minWidthImage, newWidth),
        );
        setWidth(validWidth || undefined);
        widthRef.current = validWidth;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        handleMouseUp();
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    useEffect(() => {
      if (debouncedAttrWidth) {
        setWidth(debouncedAttrWidth);
      }
    }, [debouncedAttrWidth]);

    return (
      <NodeViewWrapper className="react-component not-prose" ref={parentRef}>
        <div
          className={classNames(
            "relative flex group ",
            selected && "ring-2 ring-blue-500",
          )}
          ref={ref}
          style={{ maxWidth: width }}
        >
          <img
            ref={imgRef}
            src={node.attrs.src}
            alt="Uploaded by user"
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              objectFit: "cover",
            }}
          />
          <div
            onMouseDown={startResize}
            style={{
              cursor: "se-resize",
            }}
            className={classNames(
              "absolute h-[10px] w-[10px] -right-[6px] -bottom-[6px] bg-blue-500 hidden",
              selected && "block",
            )}
          />
        </div>
      </NodeViewWrapper>
    );
  },
);

export interface TipTapCustomImageOptions {
  width?: number;
}

export const TipTapCustomImage = (
  uploadFn: undefined | ((file: File) => Promise<string | undefined>),
) => {
  return Image.extend<TipTapCustomImageOptions>({
    addAttributes() {
      return {
        src: {
          default: null,
        },
        width: {
          default: "",
          parseHTML: () => this.options.width,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: "img",
        },
      ];
    },
    addNodeView() {
      return ReactNodeViewRenderer(ImageComponent);
    },
    addProseMirrorPlugins() {
      return uploadFn ? [uploadImagePlugin(uploadFn)] : [];
    },
  });
};

export default TipTapCustomImage;
